import React, { useEffect, useMemo, useState } from "react";

import { Close } from "@mui/icons-material";
import UndoIcon from "@mui/icons-material/Undo";
import { Button, Fade, IconButton, Modal, Stack, TextField, Typography } from "@mui/material";
import { BinDeleteIcon } from "assets/material-custom-icons";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { ValidationError } from "components/Validation-error/ValidationError";
import { debounce } from "lodash";

import { Errors, MeasureDataRound, useGroupsForm } from "../Groups-form/GroupsFormContext";

interface Props {
  dealId: number;
  rounds: Record<number, MeasureDataRound[]> | null;
  onClose: () => void;
  onSubmit: (data: { dealId: number; state: MeasureDataRound[] }) => void;
}

export const RoundsManageModal: React.FC<Props> = ({ dealId, rounds, onClose, onSubmit }) => {
  const { getDealRoundsFromState, getDealRoundsErrors } = useGroupsForm();

  const [state, setState] = useState<MeasureDataRound[]>([]);
  const [errors, setErrors] = useState<Record<number | string, string[]>>({});

  const maxStringId = useMemo<number>(() => {
    const allRounds = rounds ? [...Object.values(rounds).flat(), ...state] : [...state];

    return allRounds.reduce<number>((res, item) => {
      if (typeof item.id === "number") return res; // Skep existing rounds
      if (Number(item.id) > res) return Number(item.id); // Check created on client
      return res;
    }, 0);
  }, [rounds, state]);

  const roundsNamesError: string = useMemo(() => {
    let res = "";

    const roundsNames = state.filter((item) => !item.is_deleted).map((item) => item.name);
    const uniqueNamesArray = Array.from(new Set(roundsNames));

    if (roundsNames.length !== uniqueNamesArray.length) {
      res = "There are two or more rounds with the same name.";
    }

    return res;
  }, [state]);

  const hasErrors = useMemo<boolean>(() => {
    return Boolean(Object.entries(errors).length);
  }, [errors]);

  useEffect(() => {
    if (!dealId && state.length) {
      return setState([]);
    }

    if (dealId) {
      const rounds = getDealRoundsFromState({ dealId });
      const errors = getDealRoundsErrors(dealId);

      if (!rounds) return;
      setState(rounds);
      if (!errors) return;
      setErrors(errors);
    }
  }, [dealId]);

  const clearError = (id: string | number) => {
    if (!errors[id]) return;
    setErrors((prevState) => {
      const errorsCopy = { ...prevState };

      delete errorsCopy[id];

      return errorsCopy;
    });
  };

  const handleChangeName = debounce((id: string | number, newValue: string) => {
    setState((prevState) =>
      prevState.map((round) => {
        if (round.id !== id) return round;
        clearError(id);
        return { ...round, name: newValue };
      })
    );
  }, 500);

  const handleIsDeleted = ({ id, is_deleted }: Pick<MeasureDataRound, "id" | "is_deleted">) => {
    clearError(id);
    setState((prevState) => prevState.map((round) => (round.id === id ? { ...round, is_deleted } : round)));
  };

  const handleAddRound = () => {
    setState((prevState) => [
      ...prevState,
      { id: (maxStringId + 1).toString(), name: "New round", is_deleted: false, investors: [] }
    ]);
  };

  const memoState = useMemo(() => {
    return state.filter((item) => !item.is_deleted);
  }, [state]);

  const validate = (state: MeasureDataRound[]): void => {
    const errors = state.reduce<Record<number | string, string[]>>((acc, round) => {
      return { ...acc, ...(!round.is_deleted && !round.name && { [round.id]: ["Round name is required"] }) };
    }, {});

    if (Object.entries(errors).length) {
      setErrors(errors);
      throw Errors.VALIDATION_FAILED;
    }
  };

  const handleSubmit = () => {
    try {
      validate(state);
      onSubmit({ dealId, state });
      onClose();
    } catch (e) {
      console.log(e);
    }
  };

  return (
    <Modal onClose={onClose} open={Boolean(dealId)}>
      <Stack
        sx={{
          ...style,
          width: 457,
          gap: "20px",
          p: "10px 30px 30px 30px",
          borderRadius: "8px",
          boxShadow: "-2px 2px 15px -2px rgba(0, 0, 0, 0.25)",
          bgcolor: "white"
        }}
      >
        <Stack
          direction="row"
          sx={{
            justifyContent: "space-between",
            paddingY: "7px",
            borderBottom: "1px solid #DAE0E2",
            alignItems: "center"
          }}
        >
          <Typography sx={{ color: "#0A3142", fontSize: "16px", fontWeight: 500 }}>Add Round</Typography>
          <IconButton onClick={onClose}>
            <Close />
          </IconButton>
        </Stack>
        <Stack
          sx={{
            gap: "15px",
            minHeight: "100px",
            maxHeight: 400,
            overflow: "auto",
            p: "5px 0"
          }}
        >
          {/* {memoState.length ? ( */}
          {state.length ? (
            // memoState.map(({ id, name }, index) => (
            state.map(({ id, name, is_deleted }, index) => (
              <Fade in key={id} timeout={(index + 1) * 200}>
                <Stack direction="row" sx={{ gap: "10px" }}>
                  <TextField
                    onChange={(e) => handleChangeName(id, e.target.value.trim())}
                    defaultValue={name}
                    fullWidth
                    disabled={is_deleted}
                    label="Round Name"
                    {...(is_deleted && { sx: { input: { textDecoration: "line-through" } } })}
                    error={Boolean(errors[id])}
                    helperText={errors[id]?.[0]}
                  />
                  <MaterialTooltip title={is_deleted ? "Restore" : "Delete"} isShow placement="right">
                    <IconButton
                      sx={{ transform: "translate(0, 4px)" }}
                      disabled={!is_deleted && memoState.length < 2}
                      onClick={() => handleIsDeleted({ id, is_deleted: !is_deleted })}
                    >
                      {is_deleted ? <UndoIcon /> : <BinDeleteIcon />}
                    </IconButton>
                  </MaterialTooltip>
                </Stack>
              </Fade>
            ))
          ) : (
            <Typography>No rounds were found</Typography>
          )}
        </Stack>
        {roundsNamesError && <ValidationError messages={[roundsNamesError]} />}
        <Button
          sx={{
            alignSelf: "flex-start",
            fontSize: "15px",
            fontWeight: 400,
            textDecoration: "underline",
            ":hover": { textDecoration: "underline" }
          }}
          onClick={handleAddRound}
        >
          Add One More Round
        </Button>

        <Stack direction="row" sx={{ justifyContent: "flex-end", alignItems: "center", gap: "18px" }}>
          <Fade in timeout={300}>
            <Button variant="outlined" onClick={onClose}>
              Cancel
            </Button>
          </Fade>
          <Fade in timeout={400}>
            <Button variant="contained" onClick={handleSubmit} disabled={Boolean(roundsNamesError) || hasErrors}>
              Submit
            </Button>
          </Fade>
        </Stack>
      </Stack>
    </Modal>
  );
};

const style = {
  position: "absolute" as "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  outline: "none"
};
